import React from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import CreateEmptyAnalysisTemplateModal from 'analysis-tool/templates/CreateEmptyAnalysisTemplateModal';
import {AssaysFeature, RecipesFeature, V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {isOrganization} from 'constants/UserConstants';
import CreateFormModal from 'forms/form-definition/CreateFormModal';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {ActionOrigin, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {
  CollectionPageTemplateCollectionNode,
  TEMPLATE_COLLECTION_PAGE_LISTING_QUERY,
} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import CreateTemplateModal, {CreateSubtemplateModal} from 'notebook/entry-templates/CreateTemplateModal';
import SubtemplateUtil from 'notebook/entry-templates/SubtemplateUtil';
import * as TemplateConstants from 'notebook/entry-templates/TemplateConstants';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import {
  PLATE_DESIGN_TEMPLATE_TABLE_NAME,
  TEMPLATE_COLLECTIBLE_TYPES,
} from 'notebook/template-collections/TemplateCollectionTypes';
import PlateDesignTemplateDAO from 'plates/dao/PlateDesignTemplateDAO';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import serializeProcedureFlowchartForNetwork from 'procedures/network/serializeProcedureFlowchartForNetwork';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import CreateProcedureModal from 'procedures/templates/CreateProcedureModal';
import CreateRequestV2TemplateModal from 'processes/requests_v2/modals/CreateRequestV2TemplateModal';
import ScratchPlateDesignModal from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModal';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {typeToName} from 'util/NameUtil';

export const TEMPLATE = 'Entry template';
export const SUBTEMPLATE = 'Sub-template';
export const FORM_DEFINITION = 'Form';
export const ANALYSIS_TEMPLATE = 'Automation template';
export const RECIPE = getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.RECIPE);
export const ASSAY = getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY);
export const REQUEST_V2_DEFINITION = 'Request template';
export const PLATE_DESIGN_TEMPLATE = 'Plate design template';
export const MODAL_CONTAINER_KEY = 'TEMPLATE_COLLECTION_MODAL_CONTAINER_KEY';

/**
 * Represents how a template type appears in the Create dropdown for a given collection.
 *
 * - `hidden`: The option is not shown at all.
 * - `enabled`: The option is shown and selectable.
 * - `disabled`: The option is shown but not selectable, with a tooltip `reason` explaining why.
 */
export type CreateMenuState = {kind: 'hidden'} | {kind: 'enabled'} | {kind: 'disabled'; reason: string};

interface TemplateTypeMetadata {
  /**
   * The string that we should use in the UI to display this Template Type.
   */
  name: string;

  /**
   * The ModelTypeConstant that corresponds to this Template Type.
   */
  value: ModelTypeConstants;

  /**
   * The Template Type in string format that is used by TemplateCollectionNode query
   */
  filterValue: string;

  /**
   * Icon shown next to this Template Type in the Create dropdown.
   */
  icon: BenchlingIconName;

  /**
   * Can these be filtered in the template collection sidebar and page
   */
  isViewable: () => boolean;

  /**
   * Returns the state of this template type's option in the Create dropdown for the given collection.
   * Consolidates visibility, enablement, and disabled-reason tooltip into a single state.
   *
   * `collection` may be `null` when the option is shown outside of a specific collection (e.g. the global
   * create menu). In that case, collection-specific checks (such as organization ownership) are skipped.
   */
  getCreateMenuState: (collection: CollectionPageTemplateCollectionNode | null) => CreateMenuState;
}

type TemplateTypeWithHandler = TemplateTypeMetadata & {
  /**
   * This click handler is triggered from the Template Collection page and indicates that we want to create some instance of the given type.
   * Typically, this should open some modal where we can get additional information.
   */
  createTemplateTypeClickHandler: () => void;
};

type TemplateTypesArray = Array<TemplateTypeWithHandler>;

/**
 * Static metadata for each template collectible type, including name, value, and
 * feature flag checks, but not collection-specific creation handlers.
 */
const TEMPLATE_TYPE_METADATA: Array<TemplateTypeMetadata> = [
  {
    name: TEMPLATE,
    value: ModelTypeConstants.TEMPLATE,
    filterValue: ModelTypeConstants.TEMPLATE,
    icon: 'file-in-brackets',
    isViewable: () => true,
    getCreateMenuState: () => ({kind: 'enabled'}),
  },
  {
    name: SUBTEMPLATE,
    value: ModelTypeConstants.SUBTEMPLATE,
    filterValue: ModelTypeConstants.SUBTEMPLATE,
    icon: 'broken-columns-in-bracket',
    isViewable: () => true,
    getCreateMenuState: () => ({kind: 'enabled'}),
  },
  {
    name: ANALYSIS_TEMPLATE,
    value: ModelTypeConstants.ANALYSIS_TEMPLATE,
    filterValue: ModelTypeConstants.ANALYSIS_TEMPLATE,
    icon: 'lightbulb-in-brackets',
    isViewable: () => isAnalysisTemplatesEnabled(),
    getCreateMenuState: () =>
      isAnalysisTemplatesEnabled() && BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES
        ? {kind: 'enabled'}
        : {kind: 'hidden'},
  },
  {
    name: FORM_DEFINITION,
    value: ModelTypeConstants.FORM_DEFINITION,
    filterValue: ModelTypeConstants.FORM_DEFINITION,
    icon: 'clipboard-in-brackets',
    isViewable: () => isWorkflowCreateTaskFormsEnabled(),
    getCreateMenuState: () => (isWorkflowCreateTaskFormsEnabled() ? {kind: 'enabled'} : {kind: 'hidden'}),
  },
  {
    name: REQUEST_V2_DEFINITION,
    value: ModelTypeConstants.REQUEST_V2_DEFINITION,
    filterValue: ModelTypeConstants.REQUEST_V2_DEFINITION,
    icon: 'stubbed-plus-sign-in-brackets',
    isViewable: () => isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_OPEN}),
    getCreateMenuState: () =>
      isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_ADMIN}) ? {kind: 'enabled'} : {kind: 'hidden'},
  },
  // For procedure types, we need to check if the collection is org-owned since
  // creating procedures in personal template collections is disallowed (Workflows requires orgs)
  {
    name: ASSAY,
    value: ModelTypeConstants.ASSAY_PROCEDURE,
    filterValue: typeToName(ModelTypeConstants.ASSAY_PROCEDURE),
    icon: 'eye-dropper',
    isViewable: () => isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN}),
    getCreateMenuState: (collection: CollectionPageTemplateCollectionNode | null) => {
      if (!isFeatureEnabled({feature: AssaysFeature.ASSAYS_CREATE_ASSAY})) {
        return {kind: 'hidden'};
      }
      if (collection != null && !isOrganization(collection.ownerInfoJson)) {
        return {
          kind: 'disabled',
          reason: `${pluralize(2, ASSAY)} can only be created in an organization-owned template collection`,
        };
      }
      return {kind: 'enabled'};
    },
  },
  {
    name: RECIPE,
    value: ModelTypeConstants.RECIPE_PROCEDURE,
    filterValue: typeToName(ModelTypeConstants.RECIPE_PROCEDURE),
    icon: 'mortar-pestle',
    isViewable: () => isFeatureEnabled({feature: RecipesFeature.RECIPES_OPEN}),
    getCreateMenuState: (collection: CollectionPageTemplateCollectionNode | null) => {
      if (!isFeatureEnabled({feature: RecipesFeature.RECIPES_CREATE_RECIPE})) {
        return {kind: 'hidden'};
      }
      if (collection != null && !isOrganization(collection.ownerInfoJson)) {
        return {
          kind: 'disabled',
          reason: `${pluralize(2, RECIPE)} can only be created in an organization-owned template collection`,
        };
      }
      return {kind: 'enabled'};
    },
  },
  {
    name: PLATE_DESIGN_TEMPLATE,
    value: ModelTypeConstants.PLATE_DESIGN_TEMPLATE,
    filterValue: PLATE_DESIGN_TEMPLATE_TABLE_NAME,
    icon: 'well-plate',
    isViewable: () => BenchlingConfig.ENABLE_SCRATCH_PLATES,
    getCreateMenuState: () => (BenchlingConfig.ENABLE_SCRATCH_PLATES ? {kind: 'enabled'} : {kind: 'hidden'}),
  },
];

/**
 * Mapping of template type values to their creation handlers.
 *
 * Each handler takes a collection and returns a function that can be called to create the item.
 *
 * If called from a context that doesn't have a specific template collection, e.g. global create, the
 * collection is omitted and the user chooses one within the opened modal.
 */
const TEMPLATE_TYPE_CREATION_HANDLERS: Partial<
  Record<ModelTypeConstants, (collection?: CollectionPageTemplateCollectionNode) => () => void>
> = {
  [ModelTypeConstants.TEMPLATE]: (collection) => () => {
    createTemplateClickHandler(collection);
  },
  [ModelTypeConstants.SUBTEMPLATE]: (collection) => () => {
    createSubtemplateClickHandler(collection);
  },
  [ModelTypeConstants.ANALYSIS_TEMPLATE]: (collection) => () => {
    createAnalysisTemplateClickHandler(collection);
  },
  [ModelTypeConstants.FORM_DEFINITION]: (collection) => () => {
    createFormClickHandler(collection);
  },
  [ModelTypeConstants.REQUEST_V2_DEFINITION]: (collection) => () => {
    createRequestV2TemplateClickHandler(collection);
  },
  [ModelTypeConstants.RECIPE_PROCEDURE]: (collection) => () => {
    createProcedureClickHandler(ProcedureType.RECIPE, collection);
  },
  [ModelTypeConstants.ASSAY_PROCEDURE]: (collection) => () => {
    createProcedureClickHandler(ProcedureType.ASSAY, collection);
  },
  [ModelTypeConstants.PLATE_DESIGN_TEMPLATE]: (collection) => () => {
    createPlateDesignTemplateClickHandler(collection);
  },
};

/**
 * A template type option shown in the global create menu, decoupled from any specific template collection.
 */
export interface GlobalTemplateCreateMenuItem {
  name: string;
  icon: BenchlingIconName;
  value: ModelTypeConstants;
  handleOnClick: () => void;
}

const TemplateCollectionUtil = {
  isTemplateCollectibleModelType(modelType: string): boolean {
    return TEMPLATE_COLLECTIBLE_TYPES.includes(modelType as ModelTypeConstants);
  },

  /**
   * Returns the list of template collectible types that are viewable based on feature flags and capabilities.
   */
  getViewableTemplateCollectibleTypes(): Array<ModelTypeConstants> {
    return TEMPLATE_TYPE_METADATA.filter((type) => type.isViewable()).map((type) => type.value);
  },

  /**
   * Returns the list of Template Collectible types similar to getViewableTemplateCollectibleTypes but also
   * includes a `createTemplateTypeClickHandler` which, when called, will create a new Collectible of the
   * given type in the given Collection.
   */
  getTemplateCollectionTypes(collection: CollectionPageTemplateCollectionNode): TemplateTypesArray {
    return TEMPLATE_TYPE_METADATA.map((metadata) => {
      const creationHandler = TEMPLATE_TYPE_CREATION_HANDLERS[metadata.value];
      return {
        ...metadata,
        createTemplateTypeClickHandler: creationHandler ? creationHandler(collection) : () => {},
      };
    });
  },

  /**
   * Returns the template type options that should appear in the global create menu, derived from
   * TEMPLATE_TYPE_METADATA. Each option opens its create modal without a pre-selected collection (the user
   * chooses one within the modal). Only includes types whose create-menu state is not hidden.
   */
  getGlobalTemplateCreateMenuItems(): Array<GlobalTemplateCreateMenuItem> {
    const toMenuItem = (metadata: TemplateTypeMetadata): Array<GlobalTemplateCreateMenuItem> => {
      const creationHandler = TEMPLATE_TYPE_CREATION_HANDLERS[metadata.value];
      if (creationHandler == null || metadata.getCreateMenuState(null).kind === 'hidden') {
        return [];
      }
      return [
        {
          name: metadata.name,
          icon: metadata.icon,
          value: metadata.value,
          handleOnClick: creationHandler(),
        },
      ];
    };

    return TEMPLATE_TYPE_METADATA.flatMap((metadata) => toMenuItem(metadata));
  },
};

function handleSubmit(
  name: string,
  templateCollection: TemplateConstants.TemplateCollection,
  isProtected: boolean,
  tagSchema?: TagSchema,
  schemaTags?: TagUpdateObject
): void {
  TemplateUtil.createThenOpen(name, templateCollection.id, isProtected, tagSchema, schemaTags).done(() => {
    LegacyModalContainer.close(MODAL_CONTAINER_KEY);
  });
}

function handleSubmitSubtemplate(
  name: string,
  templateCollection: TemplateConstants.TemplateCollection
): void {
  SubtemplateUtil.createThenOpen(name, templateCollection.id).then(() => {
    LegacyModalContainer.close(MODAL_CONTAINER_KEY);
  });
}

function createTemplateClickHandler(collection?: CollectionPageTemplateCollectionNode): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateTemplateModal
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      onSubmit={handleSubmit}
      initialTemplateCollection={collection}
    />
  );
}

function createSubtemplateClickHandler(collection?: CollectionPageTemplateCollectionNode): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateSubtemplateModal
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      onSubmit={handleSubmitSubtemplate}
      initialTemplateCollection={collection}
    />
  );
}

function createFormClickHandler(collection?: CollectionPageTemplateCollectionNode): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateFormModal
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
    />
  );
}

function createRequestV2TemplateClickHandler(collection?: CollectionPageTemplateCollectionNode): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateRequestV2TemplateModal
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
      actionOrigin={ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE}
    />
  );
}

function createProcedureClickHandler(
  procedureType: ProcedureType,
  collection?: CollectionPageTemplateCollectionNode
): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateProcedureModal
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
      procedureType={procedureType}
      onCreateProcedure={async (newProcedure) => {
        const {name, templateCollectionId, flowchart} = newProcedure;
        const {
          createdIds: [id],
        } = await ProcedureDAO.createProcedure({
          name,
          templateCollectionId,
          procedureType,
          flowchart: serializeProcedureFlowchartForNetwork(flowchart, procedureType),
        });
        return id;
      }}
    />
  );
}

function createPlateDesignTemplateClickHandler(collection?: CollectionPageTemplateCollectionNode): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <ScratchPlateDesignModal
      enableCreateFromTemplate={false}
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      onRequestSubmit={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      onRefetch={() => {
        getApolloClient()
          .refetchQueries({include: [TEMPLATE_COLLECTION_PAGE_LISTING_QUERY]})
          .catch(() => {});
      }}
      initialTemplateCollectionId={collection?.id}
      onCreatePlateDesignTemplate={async (params) => {
        await PlateDesignTemplateDAO.createPlateDesignTemplateFromScratch(params);
      }}
    />
  );
}

function createAnalysisTemplateClickHandler(collection?: CollectionPageTemplateCollectionNode): void {
  LegacyModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateEmptyAnalysisTemplateModal
      onRequestClose={() => {
        LegacyModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
      actionOrigin={
        collection != null
          ? ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE
          : ActionOrigin.SIDEPANEL_GLOBAL_CREATE
      }
    />
  );
}

export default TemplateCollectionUtil;
